// Layer 5 - 5 years - Word reading, basic math, patterns, spatial pre-SD (12 games total)
import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "@/context/GameContext";
import { playSfx, speak } from "@/lib/sounds";
import { t } from "@/lib/translations";

const playSfxHelper = (name, soundOn) => {
  try {
    playSfx(name, soundOn);
  } catch (e) {
    console.warn("Sound play failed", e);
  }
};

// ==========================================
// 1) Read Words
// ==========================================
function ReadWords({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const items = {
    id: [
      { emoji: "🐱", word: "KUCING", distractors: ["AYAM", "BEBEK"] },
      { emoji: "🍎", word: "APEL", distractors: ["ROTI", "TELUR"] },
      { emoji: "🌞", word: "MATAHARI", distractors: ["BULAN", "BINTANG"] },
      { emoji: "🚗", word: "MOBIL", distractors: ["KERETA", "KAPAL"] },
    ],
    en: [
      { emoji: "🐱", word: "CAT", distractors: ["DOG", "COW"] },
      { emoji: "🍎", word: "APPLE", distractors: ["BREAD", "EGG"] },
      { emoji: "🌞", word: "SUN", distractors: ["MOON", "STAR"] },
      { emoji: "🚗", word: "CAR", distractors: ["TRAIN", "SHIP"] },
    ],
  };
  const list = items[language] || items["en"];
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const current = list[round % list.length];
  const choices = useMemo(
    () => [...current.distractors, current.word].sort(() => Math.random() - 0.5),
    [round, language, current]
  );

  const pick = (w) => {
    if (w === current.word) {
      playSfxHelper("success", soundOn);
      const s = score + 1;
      setScore(s);
      if (s >= 3) {
        setTimeout(() => onWin(), 1200);
        setScore(0);
      } else {
        setRound((r) => r + 1);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-words">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-3 text-center">
        {t("read_picture", language)}
      </div>
      <div className="bg-white rounded-3xl p-6 border-4 border-purple-200 shadow-[0_6px_0_#c084fc]">
        <div className="text-8xl text-center select-none">{current.emoji}</div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-6 max-w-2xl">
        {choices.map((w) => (
          <motion.button
            key={w}
            whileTap={{ scale: 0.92 }}
            onClick={() => pick(w)}
            className="px-4 py-3 rounded-2xl bg-white border-4 border-purple-300 font-display font-bold text-xl sm:text-2xl text-slate-700 shadow-md"
            style={{ boxShadow: "0 5px 0 #a855f7" }}
            data-testid={`word-${w}`}
          >
            {w}
          </motion.button>
        ))}
      </div>
      <div className="mt-3 font-bold text-slate-500">⭐ {score}/3</div>
    </div>
  );
}

// ==========================================
// 2) Basic Math (Addition)
// ==========================================
function BasicMath({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const { a, b, choices } = useMemo(() => {
    const a = 1 + Math.floor(Math.random() * 4);
    const b = 1 + Math.floor(Math.random() * 4);
    const ans = a + b;
    const set = new Set([ans]);
    while (set.size < 4) {
      set.add(Math.max(1, ans + Math.floor(Math.random() * 5 - 2)));
    }
    return { a, b, choices: Array.from(set).sort(() => Math.random() - 0.5) };
  }, [round]);

  const pick = (n) => {
    if (n === a + b) {
      playSfxHelper("success", soundOn);
      const s = score + 1;
      setScore(s);
      if (s >= 3) {
        setTimeout(() => onWin(), 1200);
        setScore(0);
      } else {
        setRound((r) => r + 1);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-math">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-2 text-center">
        {t("what_is_sum", language)}
      </div>
      <div className="bg-white rounded-3xl px-8 py-5 border-4 border-purple-200 shadow-[0_6px_0_#c084fc] my-3 w-full max-w-sm">
        <div className="font-display font-bold text-5xl sm:text-6xl text-center text-purple-600">
          {a} + {b} = ?
        </div>
        <div className="flex justify-center gap-3 mt-3 text-3xl select-none">
          <div>{"🍓".repeat(a)}</div>
          <div className="text-slate-400 font-bold">+</div>
          <div>{"🍓".repeat(b)}</div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mt-2">
        {choices.map((c) => (
          <motion.button
            key={c}
            whileTap={{ scale: 0.9 }}
            onClick={() => pick(c)}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white border-4 border-purple-300 font-display font-bold text-3xl text-slate-700 shadow-md"
            style={{ boxShadow: "0 5px 0 #a855f7" }}
            data-testid={`math-choice-${c}`}
          >
            {c}
          </motion.button>
        ))}
      </div>
      <div className="mt-3 font-bold text-slate-500">⭐ {score}/3</div>
    </div>
  );
}

// ==========================================
// 3) Pattern Game
// ==========================================
function PatternGame({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const symbols = ["🔴", "🔵", "🟡", "🟢"];
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);

  const { sequence, answer, choices } = useMemo(() => {
    const a = symbols[Math.floor(Math.random() * symbols.length)];
    let b;
    do {
      b = symbols[Math.floor(Math.random() * symbols.length)];
    } while (b === a);
    const sequence = [a, b, a, b];
    const answer = a;
    const choices = [a, b, symbols.find((s) => s !== a && s !== b)].sort(() => Math.random() - 0.5);
    return { sequence, answer, choices };
  }, [round]);

  const pick = (s) => {
    if (s === answer) {
      playSfxHelper("success", soundOn);
      const sc = score + 1;
      setScore(sc);
      if (sc >= 3) {
        setTimeout(() => onWin(), 1200);
        setScore(0);
      } else {
        setRound((r) => r + 1);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-pattern">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-3 text-center">
        {t("complete_pattern", language)}
      </div>
      <div className="bg-white rounded-3xl px-6 py-5 border-4 border-purple-200 shadow-[0_6px_0_#c084fc] flex items-center gap-3 select-none">
        {sequence.map((s, i) => (
          <span key={`seq-${i}-${s}`} className="text-5xl sm:text-6xl">{s}</span>
        ))}
        <span className="text-5xl sm:text-6xl font-display font-bold text-purple-400">?</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-6">
        {choices.map((c, i) => (
          <motion.button
            key={`pat-${round}-${i}-${c}`}
            whileTap={{ scale: 0.9 }}
            onClick={() => pick(c)}
            className="w-20 h-20 rounded-2xl bg-white border-4 border-purple-300 flex items-center justify-center text-5xl shadow-md"
            style={{ boxShadow: "0 5px 0 #a855f7" }}
            data-testid={`pattern-choice-${i}`}
          >
            {c}
          </motion.button>
        ))}
      </div>
      <div className="mt-3 font-bold text-slate-500">⭐ {score}/3</div>
    </div>
  );
}

// ==========================================
// 4) Balloon Subtraction (Game 4)
// ==========================================
function BalloonSubtraction({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [popped, setPopped] = useState([]);
  const balloons = [1, 2, 3, 4, 5];

  const handlePop = (id) => {
    playSfxHelper("pop", soundOn);
    setPopped((prev) => {
      const next = [...prev, id];
      if (next.length === 2) {
        playSfxHelper("success", soundOn);
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Pecahkan 2 Balon: 5 - 2 = ? 🎈" : "Pop 2 Balloons: 5 - 2 = ? 🎈"}
      </div>

      <div className="flex gap-4 p-6 rounded-3xl bg-sky-100 border-4 border-sky-300 shadow-xl min-h-[10rem] items-center justify-center w-full max-w-sm">
        {balloons.map((b) => {
          const isPopped = popped.includes(b);
          return (
            <motion.button
              key={b}
              onClick={() => handlePop(b)}
              disabled={isPopped}
              whileTap={{ scale: 0.8 }}
              className="text-5xl select-none"
              style={{ opacity: isPopped ? 0.2 : 1 }}
            >
              🎈
            </motion.button>
          );
        })}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Memvisualisasikan Operasi Pengurangan Konkrit" : "Visualizes Concrete Subtraction Operations"}
      </div>
    </div>
  );
}

// ==========================================
// 5) Animal Spelling (Game 5)
// ==========================================
function AnimalSpelling({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const targetWord = "SINGA";
  const [sequence, setSequence] = useState([]);
  const letters = ["S", "I", "N", "G", "A"];

  const handleLetter = (l) => {
    const nextIndex = sequence.length;
    if (l === letters[nextIndex]) {
      playSfxHelper("pop", soundOn);
      const nextSeq = [...sequence, l];
      setSequence(nextSeq);
      if (nextSeq.length === letters.length) {
        playSfxHelper("success", soundOn);
        setTimeout(() => onWin(), 1200);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Eja Nama Singa! 🦁" : "Spell the Lion's Name! 🦁"}
      </div>

      <div className="w-56 h-32 rounded-3xl bg-white border-4 border-purple-200 flex items-center justify-center text-7xl select-none shadow-xl">
        🦁
      </div>

      {/* Render current spelling word state */}
      <div className="font-display font-black text-4xl text-purple-600 tracking-wider">
        {sequence.join("") || "_____"}
      </div>

      <div className="flex gap-3">
        {letters.map((l, i) => {
          const isUsed = sequence.includes(l);
          return (
            <motion.button
              key={`${l}-${i}`}
              onClick={() => handleLetter(l)}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-xl bg-white border-2 border-purple-300 font-display font-black text-xl text-slate-700 shadow"
              style={{ opacity: isUsed ? 0.3 : 1 }}
              disabled={isUsed}
            >
              {l}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================
// 6) Double Gear Rotation (Game 6)
// ==========================================
function DoubleGearRotation({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleRotate = () => {
    playSfxHelper("success", soundOn);
    setSuccess(true);
    setTimeout(() => onWin(), 1200);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Putar Roda Gigi Ganda! ⚙️" : "Rotate the Double Gears! ⚙️"}
      </div>

      <div className="flex gap-8 items-center justify-center p-6 bg-slate-50 border-4 border-purple-100 rounded-3xl shadow-xl w-64 h-48 relative">
        <motion.span
          animate={success ? { rotate: 360 } : {}}
          transition={{ duration: 1 }}
          className="text-7xl select-none"
        >
          ⚙️
        </motion.span>
        <motion.span
          animate={success ? { rotate: -360 } : {}}
          transition={{ duration: 1 }}
          className="text-5xl select-none"
        >
          ⚙️
        </motion.span>
      </div>

      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={handleRotate}
        disabled={success}
        className="px-6 py-3 rounded-2xl bg-purple-500 text-white font-display font-black text-lg shadow-lg border-2 border-white"
      >
        🔄 {language === "id" ? "Putar" : "Rotate"}
      </motion.button>
    </div>
  );
}

// ==========================================
// 7) Honeybee Maze (Game 7)
// ==========================================
function HoneybeeMaze({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleDirection = (isCorrect) => {
    if (isCorrect) {
      playSfxHelper("success", soundOn);
      setSuccess(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Bantu Lebah ke Sarang Madu! 🍯" : "Guide the Bee to the Honeycomb! 🍯"}
      </div>

      <div className="w-80 h-48 bg-purple-50/50 border-4 border-purple-200 rounded-3xl p-6 flex flex-col justify-between shadow-xl relative">
        <div className="flex justify-between items-center w-full">
          <motion.span
            animate={success ? { x: 200 } : {}}
            transition={{ duration: 1 }}
            className="text-5xl select-none"
          >
            🐝
          </motion.span>
          <span className="text-5xl select-none">🍯</span>
        </div>

        <div className="flex justify-around">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => handleDirection(true)}
            disabled={success}
            className="px-4 py-2 rounded-xl bg-purple-500 text-white font-display font-black text-sm shadow"
          >
            ➡️ {language === "id" ? "Jalur Kanan" : "Right Path"}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => handleDirection(false)}
            disabled={success}
            className="px-4 py-2 rounded-xl bg-red-400 text-white font-display font-black text-sm shadow"
          >
            ⬅️ {language === "id" ? "Jalur Kiri" : "Left Path"}
          </motion.button>
        </div>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Atensi Perencanaan & Pengambilan Keputusan Spasial" : "Trains Planning & Spatial Decision-Making Attention"}
      </div>
    </div>
  );
}

// ==========================================
// 8) Complex Tangram Match (Game 8)
// ==========================================
function ComplexTangramMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleTangram = (id) => {
    if (id === "triangle") {
      playSfxHelper("success", soundOn);
      setSuccess(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Lengkapi Bentuk Kapal Geometri! ⛵" : "Complete the Geometry Sailboat! ⛵"}
      </div>

      <div className="relative w-56 h-36 bg-slate-300 rounded-3xl border-4 border-dashed border-slate-400 flex items-center justify-center">
        <span className="text-7xl select-none opacity-40">⛵</span>
        {success && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute text-7xl select-none"
          >
            ⛵
          </motion.span>
        )}
      </div>

      <div className="flex gap-4">
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={() => handleTangram("circle")}
          disabled={success}
          className="w-16 h-16 rounded-xl bg-white border-2 border-purple-200 flex items-center justify-center text-4xl shadow"
        >
          🔴
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={() => handleTangram("triangle")}
          disabled={success}
          className="w-16 h-16 rounded-xl bg-white border-2 border-purple-200 flex items-center justify-center text-4xl shadow animate-pulse"
        >
          🔺
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Visualisasi Geometri & Transformasi Spasial" : "Trains Geometry Visualization & Spatial Transformation"}
      </div>
    </div>
  );
}

// ==========================================
// 9) Pizza Fraction Match (Game 9)
// ==========================================
function PizzaFractionMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleFraction = (type) => {
    if (type === "half") {
      playSfxHelper("success", soundOn);
      setSuccess(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Cari Piring dengan Pecahan Setengah (1/2) Pizza! 🍕" : "Find Plate with Half (1/2) Fraction Pizza! 🍕"}
      </div>

      <div className="w-48 h-48 bg-amber-100 rounded-full border-4 border-amber-500 flex items-center justify-center shadow-xl overflow-hidden relative">
        <span className="text-[12rem] select-none translate-y-2 translate-x-2">🍕</span>
        {/* visual representation of exactly half cut */}
        <div className="absolute inset-0 bg-white/40 border-l-4 border-dashed border-red-500 left-1/2" />
      </div>

      <div className="flex gap-4">
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={() => handleFraction("quarter")}
          disabled={success}
          className="px-6 py-3 rounded-2xl bg-white border-4 border-purple-200 font-display font-black text-xl text-slate-700 shadow"
        >
          1/4
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={() => handleFraction("half")}
          disabled={success}
          className="px-6 py-3 rounded-2xl bg-white border-4 border-purple-200 font-display font-black text-xl text-slate-700 shadow animate-pulse"
        >
          1/2
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Mengenalkan Konsep Pecahan Numerik Matematika Awal" : "Introduces Early Mathematical Fraction Concepts"}
      </div>
    </div>
  );
}

// ==========================================
// 10) Fast Mole Whack (Game 10)
// ==========================================
function FastMoleWhack({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [activeHole, setActiveHole] = useState(0);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);

  const moles = [
    { num: 2, isEven: true },
    { num: 3, isEven: false },
    { num: 4, isEven: true },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveHole(Math.floor(Math.random() * 3));
    }, 1600);
    return () => clearInterval(timer);
  }, []);

  const handleHit = (idx) => {
    const target = moles[idx];
    if (idx === activeHole && target.isEven) {
      playSfxHelper("success", soundOn);
      const next = score + 1;
      setScore(next);
      if (next >= 3) {
        setTimeout(() => onWin(), 1200);
      } else {
        setRound((r) => r + 1);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Pukul Tikus dengan Angka Genap! 🐹" : "Whack the Mole with Even Number! 🐹"} ({score}/3)
      </div>

      <div className="flex gap-4 p-6 rounded-3xl bg-purple-50 border-4 border-purple-200 shadow-xl justify-around w-full max-w-sm">
        {moles.map((m, idx) => {
          const isActive = idx === activeHole;
          return (
            <motion.button
              key={idx}
              onClick={() => handleHit(idx)}
              whileTap={{ scale: 0.8 }}
              className={`w-20 h-20 rounded-full border-4 shadow-md flex flex-col items-center justify-center font-display font-black text-2xl transition-colors ${
                isActive ? "bg-amber-100 border-amber-400" : "bg-slate-700 border-slate-800 text-white/20"
              }`}
            >
              <span>{isActive ? "🐹" : "🕳️"}</span>
              {isActive && <span className="text-xs text-slate-700 mt-1">{m.num}</span>}
            </motion.button>
          );
        })}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Kontrol Atensi Kognitif (Go/No-Go Task)" : "Trains Cognitive Inhibitory Control (Go/No-Go Task)"}
      </div>
    </div>
  );
}

// ==========================================
// 11) Angklung Melody Maker (Game 11)
// ==========================================
function AngklungMelodyMaker({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [played, setPlayed] = useState([]);
  const notes = [
    { id: 1, label: "Do", color: "bg-red-400", hex: "#EF4444" },
    { id: 2, label: "Re", color: "bg-yellow-400", hex: "#F59E0B" },
    { id: 3, label: "Mi", color: "bg-green-400", hex: "#10B981" },
    { id: 4, label: "Fa", color: "bg-sky-400", hex: "#06B6D4" },
  ];

  const handlePlay = (id) => {
    playSfxHelper("star", soundOn);
    setPlayed((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      if (next.length === notes.length) {
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk 4 Angklung Berurutan! 🎋" : "Play 4 Angklung Notes in Sequence! 🎋"} ({played.length}/4)
      </div>

      <div className="flex gap-4 items-end justify-center min-h-[12rem]">
        {notes.map((n) => (
          <motion.button
            key={n.id}
            onClick={() => handlePlay(n.id)}
            whileTap={{ rotate: [-10, 10, -10, 0], scale: 0.95 }}
            className={`w-14 h-36 ${n.color} rounded-t-3xl border-t-8 border-x-4 border-white/60 shadow-xl flex flex-col justify-end pb-4 items-center`}
          >
            <span className="text-white font-display font-black text-lg select-none">{n.label}</span>
          </motion.button>
        ))}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Mengasah Memori Kerja Auditorik (Auditory Working Memory)" : "Refines Auditory Working Memory Training"}
      </div>
    </div>
  );
}

// ==========================================
// 12) Rainbow Paint Sweeper (Game 12)
// ==========================================
function RainbowPaintSweeper({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [revealed, setRevealed] = useState(false);
  const [patches, setPatches] = useState(() =>
    Array.from({ length: 9 }, (_, i) => ({
      id: i,
      wiped: false,
    }))
  );

  const paintPatch = (id) => {
    if (revealed) return;
    playSfxHelper("pop", soundOn);
    setPatches((prev) => {
      const next = prev.map((p) => (p.id === id ? { ...p, wiped: true } : p));
      const wipedCount = next.filter((p) => p.wiped).length;
      if (wipedCount === prev.length) {
        setRevealed(true);
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Usap Kuas Cat Kaca Pelangi! 🖌️" : "Sweep the Paint Brush on Rainbow Glass! 🖌️"}
      </div>

      <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-3xl overflow-hidden border-4 border-purple-200 shadow-2xl bg-gradient-to-br from-indigo-300 via-purple-200 to-pink-300 flex items-center justify-center">
        <motion.div
          animate={revealed ? { scale: [1, 1.25, 1], rotate: [0, 15, -15, 0] } : {}}
          transition={{ duration: 1 }}
          className="text-8xl select-none"
        >
          🏰
        </motion.div>

        {!revealed && (
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5 bg-slate-500/50">
            {patches.map((p) => (
              <motion.button
                key={p.id}
                onMouseEnter={() => paintPatch(p.id)}
                onTouchStart={() => paintPatch(p.id)}
                onClick={() => paintPatch(p.id)}
                animate={{ opacity: p.wiped ? 0 : 1 }}
                className="w-full h-full bg-slate-300 border border-slate-400/40 flex items-center justify-center text-4xl select-none"
              >
                🖌️
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Meningkatkan Kekuatan Otot Pergelangan Tangan & Kontrol Halus" : "Improves Wrist Muscle Strength & Fine Coordination"}
      </div>
    </div>
  );
}

// Export Layer 5 games list
export const Layer5Games = {
  words: { icon: "📖", Component: ReadWords },
  math: { icon: "➕", Component: BasicMath },
  pattern: { icon: "🔁", Component: PatternGame },
  subtraction: { icon: "🎈", Component: BalloonSubtraction },
  spelling: { icon: "🔠", Component: AnimalSpelling },
  double_gear: { icon: "⚙️", Component: DoubleGearRotation },
  honey_maze: { icon: "🍯", Component: HoneybeeMaze },
  tangram_match: { icon: "🧱", Component: ComplexTangramMatch },
  pizza_fraction: { icon: "🍕", Component: PizzaFractionMatch },
  mole_whack: { icon: "🐹", Component: FastMoleWhack },
  angklung_melody: { icon: "🎋", Component: AngklungMelodyMaker },
  rainbow_paint: { icon: "🖌️", Component: RainbowPaintSweeper },
};
